import { useEffect, useState, useCallback } from "react";
import { api, ApiError } from "@/lib/api";
import { Badge, Button, ErrorNote, Panel, errorMessage } from "@/components/ui";
import { daysUntil, fmtDate, fmtSalary, fromDateInput, REMOTE_LABEL } from "@/lib/format";
import type { Job } from "@/lib/types";

const MAX_EXPIRY_DAYS = 90;

function statusBadge(j: Job) {
  const s = j.effectiveStatus ?? j.status;
  if (s === "active") {
    const left = daysUntil(j.expiresAt);
    // Avisa cuando quedan menos de 7 días: es la ventana en la que el
    // reclutador todavía puede reaccionar antes de que desaparezca.
    return left <= 7 ? (
      <Badge tone="warn">caduca en {left} d</Badge>
    ) : (
      <Badge tone="ok">activa</Badge>
    );
  }
  if (s === "paused") return <Badge tone="mute">pausada</Badge>;
  return <Badge tone="danger">caducada</Badge>;
}

export function JobsList({
  onEdit,
  onNew,
  onApplications,
}: {
  onEdit: (id: number) => void;
  onNew: () => void;
  onApplications: (id: number) => void;
}) {
  const [items, setItems] = useState<Job[]>([]);
  const [newByJob, setNewByJob] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [list, summary] = await Promise.all([
        api<{ items: Job[] }>("/api/recruiter/jobs"),
        api<{ newByJob: Record<string, number> }>("/api/recruiter/applications/summary"),
      ]);
      setItems(list.items);
      setNewByJob(summary.newByJob);
      setError(null);
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "unknown"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function setStatus(j: Job, action: "pause" | "reactivate") {
    const body: { action: string; expiresAt?: number } = { action };
    if (action === "reactivate" && daysUntil(j.expiresAt) <= 0) {
      // Reactivar una oferta ya caducada exige fecha nueva (y el servidor
      // vuelve a aplicar el tope de 3 meses contado desde ahora).
      const suggested = new Date();
      suggested.setDate(suggested.getDate() + 30);
      const input = window.prompt(
        `Nueva fecha de caducidad (AAAA-MM-DD). Máximo ${MAX_EXPIRY_DAYS} días desde hoy.`,
        suggested.toISOString().slice(0, 10),
      );
      if (!input) return;
      const sec = fromDateInput(input);
      if (!sec) {
        setError("Fecha no válida.");
        return;
      }
      body.expiresAt = sec;
    }
    try {
      await api(`/api/recruiter/jobs/${j.id}/status`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      await load();
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "unknown"));
    }
  }

  async function remove(j: Job) {
    if (
      !window.confirm(
        `¿Borrar "${j.title}"? Se eliminarán también sus ${j.applicationCount} candidatura(s) y los CV adjuntos. No se puede deshacer.`,
      )
    ) {
      return;
    }
    try {
      await api(`/api/recruiter/jobs/${j.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "unknown"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-mono text-lg">Mis ofertas</h2>
        <Button onClick={onNew}>+ nueva oferta</Button>
      </div>

      <ErrorNote>{error}</ErrorNote>

      {loading && <p className="text-sm text-mute">Cargando…</p>}

      {!loading && items.length === 0 && (
        <Panel>
          <p className="text-sm text-mute">
            Todavía no has publicado ninguna oferta. Pulsa «nueva oferta» para empezar.
          </p>
        </Panel>
      )}

      <ul className="space-y-3">
        {items.map((j) => {
          const pending = newByJob[String(j.id)] ?? 0;
          const salary = fmtSalary(j);
          const effective = j.effectiveStatus ?? j.status;
          return (
            <li key={j.id}>
              <Panel>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-mono text-base text-ink">{j.title}</h3>
                      {statusBadge(j)}
                    </div>
                    <p className="mt-1 text-sm text-mute">
                      {j.company} · {j.sectorLabel} · {REMOTE_LABEL[j.remote]}
                      {j.location ? ` · ${j.location}` : ""}
                    </p>
                    <p className="mt-1 text-xs text-mute">
                      Publicada {fmtDate(j.publishedAt)} · caduca {fmtDate(j.expiresAt)}
                      {salary ? ` · ${salary}` : ""}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button variant="ghost" onClick={() => onApplications(j.id)}>
                      candidaturas ({j.applicationCount})
                      {pending > 0 && <span className="ml-1 text-accent">·{pending} nuevas</span>}
                    </Button>
                    <Button variant="ghost" onClick={() => onEdit(j.id)}>
                      editar
                    </Button>
                    {effective === "active" ? (
                      <Button variant="ghost" onClick={() => void setStatus(j, "pause")}>
                        pausar
                      </Button>
                    ) : (
                      <Button variant="ghost" onClick={() => void setStatus(j, "reactivate")}>
                        reactivar
                      </Button>
                    )}
                    <Button variant="danger" onClick={() => void remove(j)}>
                      borrar
                    </Button>
                  </div>
                </div>
              </Panel>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
